// src/pages/AppVersionPage.js
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig'; 
import { Save, Info, Smartphone, AlertTriangle, Download, Users } from 'lucide-react';


export default function AppVersionPage() {
    const [latestVersion, setLatestVersion] = useState('');
  const [minVersion, setMinVersion] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [partners, setPartners] = useState([]); // 👈 TAMBAHAN: State untuk menyimpan data mitra

  // Load data dari Firestore saat halaman dibuka
  useEffect(() => {
    fetchVersions();
    
    // 👈 PERBAIKAN: Ambil data Terapis, Cleaner, dan Driver dengan aman
    const fetchMitraData = async () => {
      try {
        const { getDocs } = await import('firebase/firestore'); // Pastikan getDocs dipanggil
        
        const [snapT, snapC, snapD] = await Promise.all([
          getDocs(collection(db, 'therapists')),
          getDocs(collection(db, 'cleaners')),
          getDocs(collection(db, 'drivers')) // Tambahan armada logistik
        ]);

        const tData = snapT.docs.map(d => ({ id: d.id, ...d.data(), divisi: 'Therapist' }));
        const cData = snapC.docs.map(d => ({ id: d.id, ...d.data(), divisi: 'Cleaner' }));
        const dData = snapD.docs.map(d => ({ id: d.id, ...d.data(), divisi: 'Driver' }));

        setPartners([...tData, ...cData, ...dData]); // Gabungkan semua mitra
      } catch (error) {
        console.error("Gagal memuat data mitra untuk Excel:", error);
      }
    };

    fetchMitraData();
  }, []);

  const fetchVersions = async () => {
    setLoading(true);
    try {
      const docRef = doc(db, 'app_settings', 'mitra_version');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setLatestVersion(data.latest_version || '');
        setMinVersion(data.min_version || '');
      }
    } catch (error) {
      console.error('Error fetching versions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!latestVersion || !minVersion) {
      return alert('Mohon isi kedua kolom versi!');
    }

    setSaving(true);
    try {
      // Menyimpan ke document 'mitra_version' di dalam collection 'app_settings'
      const docRef = doc(db, 'app_settings', 'mitra_version');
      await setDoc(
        docRef,
        {
          latest_version: latestVersion,
          min_version: minVersion,
          updatedAt: new Date(),
        },
        { merge: true } // Gunakan merge agar jika dokumen belum ada, otomatis terbuat
      );
      alert('✅ Berhasil memperbarui versi aplikasi Mitra!');
    } catch (error) {
      console.error('Error saving versions:', error);
      alert('❌ Gagal menyimpan data: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

    // 👈 PERBAIKAN: Fungsi Export ke Excel Dinamis
  const handleExportExcel = async () => {
    if (partners.length === 0) return alert("Tidak ada data mitra untuk diekspor.");

    try {
      // 1. Panggil library secara dinamis agar aman
      const XLSX = await import('xlsx');

      // 2. Mapping data agar sesuai dengan kebutuhan kolom Excel
      const dataToExport = partners.map(p => {
        let lastLoginStr = '-';
        if (p.lastLogin) {
          const dateVal = p.lastLogin.seconds ? p.lastLogin.seconds * 1000 : p.lastLogin;
          lastLoginStr = new Date(dateVal).toLocaleString('id-ID', { 
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' 
          });
        }

        return {
          "Nama Mitra": p.nama || p.name || '-',
          "ID Mitra": p.id_number || p.id || '-',
          "Divisi": p.divisi || '-',
          "Versi Aplikasi": p.appVersion || p.app_version || 'Tidak Diketahui',
          "Terakhir Login": lastLoginStr
        };
      });

      // 3. Susun struktur Excel
      const wsData = [
        ["LAPORAN VERSI APLIKASI & LOGIN MITRA ZIMO"],
        [`Tanggal Export: ${new Date().toLocaleString('id-ID')}`],
        [""], 
        ["Nama Mitra", "ID Mitra", "Divisi", "Versi Aplikasi", "Terakhir Login"]
      ];

      const finalData = [...wsData, ...dataToExport.map(item => Object.values(item))];
      const ws = XLSX.utils.aoa_to_sheet(finalData);

      // 4. Atur lebar kolom
      ws['!cols'] = [
        { wch: 25 }, { wch: 20 }, { wch: 15 }, { wch: 18 }, { wch: 25 }
      ];

      ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } }
      ];

      // 5. Buat File & Download
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Versi App Mitra");
      XLSX.writeFile(wb, `Laporan_Versi_Aplikasi_Mitra_${new Date().toISOString().split('T')[0]}.xlsx`);

    } catch (err) {
      console.error("Export Excel Error:", err);
      alert("Gagal mengunduh Excel. Pastikan modul xlsx telah terinstal.");
    }
  };

  if (loading) {
    return <div className="p-6 text-gray-400">Memuat pengaturan versi...</div>;
  }

  return (
    <div className="p-6 text-gray-100 max-w-3xl">
      <h2 className="text-2xl font-bold mb-6 flex items-center gap-2 text-blue-400">
        <Smartphone size={28} />
        Versi Aplikasi Mitra
      </h2>

      {/* Info Panel */}
      <div className="bg-blue-900/20 border border-blue-800 p-4 rounded-xl mb-6">
        <h3 className="flex items-center gap-2 text-blue-400 font-semibold mb-2">
          <Info size={18} /> Panduan Update
        </h3>
        <ul className="list-disc list-inside text-sm text-gray-300 space-y-2 ml-1">
          <li>
            <strong className="text-white">Versi Terbaru:</strong> Memunculkan notifikasi <i>opsional</i> (bisa dilewati) kepada mitra bahwa ada versi aplikasi yang lebih baru.
          </li>
          <li>
            <strong className="text-white">Versi Minimal:</strong> Memunculkan notifikasi <i>wajib</i> (Force Update). Jika versi aplikasi mitra berada di bawah versi ini, mereka <b>tidak bisa menggunakan aplikasi</b> sampai melakukan update di Play Store.
          </li>
        </ul>
      </div>

      {/* Form Input */}
      <form onSubmit={handleSave} className="bg-gray-900 border border-gray-800 p-6 rounded-xl shadow-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          
          {/* Input Versi Terbaru */}
          <div>
            <label className="flex text-gray-300 font-semibold mb-2 items-center gap-2">
              Versi Terbaru (Latest Version)
            </label>
            <input
              type="text"
              placeholder="Contoh: 1.0.5"
              value={latestVersion}
              onChange={(e) => setLatestVersion(e.target.value)}
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-2">
              Versi terkini yang rilis di Google Play Store.
            </p>
          </div>

          {/* Input Versi Minimal */}
          <div>
            <label className="flex text-red-400 font-semibold mb-2 items-center gap-2">
              <AlertTriangle size={16} />
              Versi Minimal (Force Update)
            </label>
            <input
              type="text"
              placeholder="Contoh: 1.0.3"
              value={minVersion}
              onChange={(e) => setMinVersion(e.target.value)}
              className="w-full p-3 bg-gray-800 border border-red-900/50 rounded-lg text-white focus:outline-none focus:border-red-500"
            />
            <p className="text-xs text-gray-500 mt-2">
              Batas versi paling bawah yang diizinkan beroperasi.
            </p>
          </div>
        </div>

                <button
          type="submit"
          disabled={saving}
          className="flex items-center justify-center gap-2 w-full md:w-auto px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
        >
          <Save size={18} />
          {saving ? 'Menyimpan...' : 'Simpan Pengaturan'}
        </button>
      </form>

      {/* 👈 TAMBAHAN: Section Export Excel Data Mitra */}
      <div className="bg-gray-900 border border-gray-800 p-6 rounded-xl shadow-lg mt-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <h3 className="flex items-center gap-2 text-white font-semibold text-lg">
            <Users size={20} className="text-green-400" />
            Data Mitra & Versi Aplikasi
          </h3>
          <button 
            onClick={handleExportExcel}
            disabled={partners.length === 0}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg text-white font-semibold transition-colors disabled:opacity-50"
          >
            <Download size={16} />
            Export ke Excel ({partners.length} Mitra)
          </button>
        </div>
        <p className="text-xs text-gray-500">
          Klik tombol di atas untuk mengunduh data nama, ID, divisi, versi aplikasi yang dipakai mitra, dan riwayat login terakhir dalam format Excel.
        </p>
      </div>
      {/* AKHIR TAMBAHAN */}

    </div>
  );
}